import { formatDistanceToNow } from 'date-fns';
import { ThumbsUp, ThumbsDown, Trash, User } from 'lucide-react';
import { useState } from 'react';
import type { Comment as CommentType } from '@/types/database.types';

interface CommentProps {
  comment: CommentType;
  onDelete?: (id: string) => void;
  onReaction: (id: string, type: 'like' | 'dislike') => void;
  isOwner: boolean;
}

const DEFAULT_AVATAR = 'https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png?20150327203541';

export default function Comment({ comment, onDelete, onReaction, isOwner }: CommentProps) {
  const [imageError, setImageError] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(comment.user?.avatar_url || DEFAULT_AVATAR);

  const handleImageError = () => {
    setImageError(true);
    setAvatarUrl(DEFAULT_AVATAR);
  };

  // Reset image error state when comment or avatar URL changes
  if (comment.user?.avatar_url && comment.user.avatar_url !== avatarUrl && !imageError) {
    setAvatarUrl(comment.user.avatar_url);
    setImageError(false);
  }

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={comment.user?.username || 'Anonymous'}
              className="h-8 w-8 sm:h-10 sm:w-10 rounded-full object-cover ring-2 ring-white"
              onError={handleImageError}
            />
          ) : (
            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-gray-200 flex items-center justify-center ring-2 ring-white">
              <User className="h-5 w-5 sm:h-6 sm:w-6 text-gray-500" />
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-gray-900 truncate">
                  {comment.user?.username || 'Anonymous'}
                </span>
                <span className="text-sm text-gray-500">
                  {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                </span>
                <span className="text-sm text-primary-600 bg-primary-50 px-2 py-0.5 rounded">
                  {comment.category}
                </span>
              </div>
              <p className="text-gray-600 whitespace-pre-wrap break-words">{comment.content}</p>
            </div>
            {isOwner && onDelete && (
              <button
                onClick={() => onDelete(comment.id)}
                className="text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
                title="Delete comment"
              >
                <Trash className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex items-center space-x-4 mt-4">
            <button
              onClick={() => onReaction(comment.id, 'like')}
              className={`inline-flex items-center text-sm transition-colors ${
                comment.userReaction === 'like'
                  ? 'text-primary-600'
                  : 'text-gray-500 hover:text-primary-600'
              }`}
              title={comment.userReaction === 'like' ? 'Remove like' : 'Like'}
            >
              <ThumbsUp
                className="h-4 w-4"
                fill={comment.userReaction === 'like' ? 'currentColor' : 'none'}
              />
              <span className="ml-1">{comment.likes_count}</span>
            </button>
            <button
              onClick={() => onReaction(comment.id, 'dislike')}
              className={`inline-flex items-center text-sm transition-colors ${
                comment.userReaction === 'dislike'
                  ? 'text-red-600'
                  : 'text-gray-500 hover:text-red-600'
              }`}
              title={comment.userReaction === 'dislike' ? 'Remove dislike' : 'Dislike'}
            >
              <ThumbsDown
                className="h-4 w-4"
                fill={comment.userReaction === 'dislike' ? 'currentColor' : 'none'}
              />
              <span className="ml-1">{comment.dislikes_count}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}